import { get, post, put } from '../../common/constants'
import { formatDate1 } from '../../common/util'

/**
 * Shared vocabulary of the Data Archival page — mirrors the backend archive engine
 * (proship_mongo, /api/tools/archive/collections, ROLE_PROSHIPADMIN).
 *
 * Tiers: MAIN (hot, the live collection) → WARM (<name>_archive, same DB) → S3 (cold, gzip JSONL under
 * mongo-archive/collection=<name>/year=YYYY/month=MM/). One RUN row per collection-month moved; one SUMMARY row
 * per collection with the sizes per tier. Timestamps arrive as IST local date-times without a zone.
 */

const BASE = 'tools/archive/collections'
const enc = (v) => encodeURIComponent(v)

export const archivalApi = {
    getSummary: () => get(`${BASE}/summary`),
    refreshSummary: () => post(`${BASE}/summary/refresh`),
    getRuns: (params) => get(`${BASE}/runs`, params),
    runNow: (name) => post(`${BASE}/${enc(name)}/run`),
    savePolicy: (name, policy) => put(`${BASE}/${enc(name)}/policy`, policy),
    rehydrate: (name, period) => post(`${BASE}/${enc(name)}/rehydrate?period=${enc(period)}`),
    getRunLinks: (id) => get(`${BASE}/runs/${enc(id)}/links`),
    cancelRun: (id) => post(`${BASE}/runs/${enc(id)}/cancel`),
}

/** Server error text (Spring's `message`, then `error`, then a plain-text body), else the fallback. */
export function errorText(err, fallback = 'Something went wrong') {
    const data = err?.response?.data
    if (typeof data === 'string' && data.trim()) return data
    return data?.message || data?.error || err?.message || fallback
}

export const RUN_STATUSES = ['QUEUED', 'RUNNING', 'SUCCESS', 'FAILED', 'ABORTED', 'DRY_RUN']
export const ACTIVE_STATUSES = ['QUEUED', 'RUNNING']

/** Run status → key of sharedStyles.statusBadgeSx. */
const STATUS_TONE = {
    QUEUED: 'pending',
    RUNNING: 'in_progress',
    SUCCESS: 'success',
    FAILED: 'failed',
    ABORTED: 'neutral',
    DRY_RUN: 'info',
}
export const statusTone = (s) => STATUS_TONE[s] || 'neutral'
/** Sentence case: DRY_RUN → "Dry run". */
export const statusLabel = (s) => {
    if (!s) return '—'
    const t = String(s).replace(/_/g, ' ').toLowerCase()
    return t.charAt(0).toUpperCase() + t.slice(1)
}

/** A queued run cancelled from this page ends as ABORTED with error "cancelled by <user>". */
const CANCELLED_RE = /^cancelled by\s*/i
export const isCancelled = (row) => row?.status === 'ABORTED' && CANCELLED_RE.test(row?.error || '')
export const cancelledBy = (row) => (isCancelled(row) ? String(row.error).replace(CANCELLED_RE, '').trim() : '')

/** "ayush.gupta@prozo.com" → "ayush.gupta"; CRON etc. unchanged. */
export const shortUser = (v) => (v ? String(v).split('@')[0] : '')

export const TIER_LABEL = { MAIN: 'Main', WARM: 'Archive', S3: 'S3' }
export const tierLabel = (t) => TIER_LABEL[t] || t || '—'

export const fmtNum = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : Math.round(Number(v)).toLocaleString('en-US'))

/** Decimal units (1 GB = 1e9 bytes), e.g. 11680000000 → "11.68 GB". */
export function fmtBytes(v) {
    if (v == null || Number.isNaN(Number(v))) return '—'
    const n = Number(v)
    const units = [
        { size: 1e12, label: 'TB' },
        { size: 1e9, label: 'GB' },
        { size: 1e6, label: 'MB' },
        { size: 1e3, label: 'KB' },
    ]
    const u = units.find((x) => Math.abs(n) >= x.size)
    return u ? `${(n / u.size).toFixed(2)} ${u.label}` : `${n} B`
}

export const fmtDay = (v) => (v ? formatDate1(v, 'dd MMM yyyy') : '—')
export const fmtDateTime = (v) => (v ? formatDate1(v, 'dd MMM yyyy, HH:mm') : '—')

/** "1h 04m", "3m 12s", "45s" between two local date-times; open-ended (still running) counts to now. */
export function fmtDuration(start, end) {
    if (!start) return '—'
    const a = new Date(start).getTime()
    const b = end ? new Date(end).getTime() : Date.now()
    if (Number.isNaN(a) || Number.isNaN(b)) return '—'
    const secs = Math.max(0, Math.round((b - a) / 1000))
    const h = Math.floor(secs / 3600)
    const m = Math.floor((secs % 3600) / 60)
    const s = secs % 60
    if (h) return `${h}h ${String(m).padStart(2, '0')}m`
    if (m) return `${m}m ${String(s).padStart(2, '0')}s`
    return `${s}s`
}

export const PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/

/**
 * Space Mongo has freed in a collection (and its archive) but not returned to the disk ("file bytes available for
 * reuse"). Only a compact gives it back; null when the collection has no summary yet.
 */
export const reclaimableBytes = (summary) => (summary
    ? Number(summary.main?.freeBytes || 0) + Number(summary.warm?.freeBytes || 0)
    : null)

/** Sum of one numeric field across collections; null when no collection has a value yet. */
export function sumOf(items, pick) {
    let total = null
    items.forEach((it) => {
        const v = pick(it)
        if (v != null && !Number.isNaN(Number(v))) total = (total || 0) + Number(v)
    })
    return total
}

/**
 * A collection's mode, shown as ONE status everywhere. Stored as two backend flags: Paused = enabled false (dryRun is
 * left as it was, so Resume returns to the previous mode); Dry run = enabled + dryRun; Live = enabled, not dryRun.
 */
export const MODE_META = {
    PAUSED: { label: 'Paused', tone: 'neutral', help: 'Nothing runs, neither the monthly job nor Run now.' },
    DRY_RUN: { label: 'Dry run', tone: 'info', help: 'Runs only count what would move. Nothing is copied, deleted or uploaded.' },
    LIVE: { label: 'Live', tone: 'success', help: 'Runs move data out of the collection.' },
}
export const policyMode = (p) => (!p?.enabled ? 'PAUSED' : p.dryRun ? 'DRY_RUN' : 'LIVE')
export const resumeModeLabel = (p) => (p?.dryRun ? 'dry run' : 'live')

/** True when this collection's data ends in S3 (both the global tier and the policy allow it). */
export const sendsToS3 = (policy, globalS3Enabled) => Boolean(globalS3Enabled) && policy?.s3Enabled !== false
